/**
 * NavigatorX Bulk Actions Panel
 * 
 * Component for performing bulk operations on selected users:
 * - Multi-select with checkbox
 * - Bulk extend trial
 * - Bulk suspend/unsuspend
 * - Bulk delete
 * - Bulk export to CSV
 * - Bulk send notification
 * 
 * @author Claude AI for TGE.LA
 * @version 1.0.0
 */

import { useState } from 'react';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import {
  X,
  Clock,
  Ban,
  Trash2,
  Download,
  Bell,
  CheckCircle,
  Loader2,
  AlertTriangle,
  Users,
  ChevronDown
} from 'lucide-react';
import { toast } from 'sonner';
import { 
  extendTrial, 
  suspendUser, 
  unsuspendUser, 
  deleteUser,
  sendUserNotification
} from '../../../utils/services/navigatorXService';
import { auth } from '../../../utils/firebase/client';
import type { NavUser } from '../../../types/navigatorx';

interface BulkActionsPanelProps {
  selectedUsers: NavUser[];
  onClearSelection: () => void;
  onRefresh: () => void;
  onRemoveFromSelection: (userId: string) => void;
}

type BulkAction = 'extend_trial' | 'suspend' | 'unsuspend' | 'delete' | 'notify' | 'export';

export default function BulkActionsPanel({
  selectedUsers,
  onClearSelection,
  onRefresh,
  onRemoveFromSelection
}: BulkActionsPanelProps) {
  const [loading, setLoading] = useState<BulkAction | null>(null);
  const [showConfirm, setShowConfirm] = useState<BulkAction | null>(null);
  const [trialDays, setTrialDays] = useState(7);
  const [progress, setProgress] = useState({ current: 0, total: 0, failed: 0 });

  const adminId = auth.currentUser?.uid || 'unknown';

  if (selectedUsers.length === 0) return null;

  // Bulk Extend Trial
  const handleBulkExtendTrial = async () => {
    setLoading('extend_trial');
    setProgress({ current: 0, total: selectedUsers.length, failed: 0 });
    
    let failed = 0;
    for (let i = 0; i < selectedUsers.length; i++) {
      try {
        await extendTrial(selectedUsers[i].id, trialDays, adminId);
        setProgress(p => ({ ...p, current: i + 1 }));
      } catch (error) {
        console.error(`Failed to extend trial for ${selectedUsers[i].id}:`, error);
        failed++;
        setProgress(p => ({ ...p, current: i + 1, failed: p.failed + 1 }));
      }
    }

    setLoading(null);
    setShowConfirm(null);
    
    if (failed === 0) {
      toast.success(`Extended trial by ${trialDays} days for ${selectedUsers.length} users`);
    } else {
      toast.warning(`Extended trial for ${selectedUsers.length - failed} users, ${failed} failed`);
    }
    
    onRefresh();
    onClearSelection();
  };

  // Bulk Suspend
  const handleBulkSuspend = async () => {
    setLoading('suspend');
    setProgress({ current: 0, total: selectedUsers.length, failed: 0 });
    
    let failed = 0;
    for (let i = 0; i < selectedUsers.length; i++) {
      try {
        await suspendUser(selectedUsers[i].id, 'Bulk admin action', adminId);
        setProgress(p => ({ ...p, current: i + 1 }));
      } catch (error) {
        console.error(`Failed to suspend ${selectedUsers[i].id}:`, error);
        failed++;
        setProgress(p => ({ ...p, current: i + 1, failed: p.failed + 1 }));
      }
    }

    setLoading(null);
    setShowConfirm(null);
    
    if (failed === 0) {
      toast.success(`Suspended ${selectedUsers.length} users`);
    } else {
      toast.warning(`Suspended ${selectedUsers.length - failed} users, ${failed} failed`);
    }
    
    onRefresh();
    onClearSelection();
  };

  // Bulk Unsuspend
  const handleBulkUnsuspend = async () => {
    setLoading('unsuspend');
    setProgress({ current: 0, total: selectedUsers.length, failed: 0 });
    
    const suspendedUsers = selectedUsers.filter(u => u.subscriptionStatus === 'BLOCKED');
    
    let failed = 0;
    for (let i = 0; i < suspendedUsers.length; i++) {
      try {
        await unsuspendUser(suspendedUsers[i].id, adminId);
        setProgress(p => ({ ...p, current: i + 1 }));
      } catch (error) {
        console.error(`Failed to unsuspend ${suspendedUsers[i].id}:`, error);
        failed++;
        setProgress(p => ({ ...p, current: i + 1, failed: p.failed + 1 }));
      }
    }

    setLoading(null);
    setShowConfirm(null);
    
    if (failed === 0) {
      toast.success(`Unsuspended ${suspendedUsers.length} users`);
    } else {
      toast.warning(`Unsuspended ${suspendedUsers.length - failed} users, ${failed} failed`);
    }
    
    onRefresh();
    onClearSelection();
  };

  // Bulk Delete
  const handleBulkDelete = async () => {
    setLoading('delete');
    setProgress({ current: 0, total: selectedUsers.length, failed: 0 });
    
    let failed = 0;
    for (let i = 0; i < selectedUsers.length; i++) {
      try {
        await deleteUser(selectedUsers[i].id, adminId);
        setProgress(p => ({ ...p, current: i + 1 }));
      } catch (error) {
        console.error(`Failed to delete ${selectedUsers[i].id}:`, error);
        failed++;
        setProgress(p => ({ ...p, current: i + 1, failed: p.failed + 1 }));
      }
    }

    setLoading(null);
    setShowConfirm(null);
    
    if (failed === 0) {
      toast.success(`Deleted ${selectedUsers.length} users`);
    } else {
      toast.warning(`Deleted ${selectedUsers.length - failed} users, ${failed} failed`);
    }
    
    onRefresh();
    onClearSelection();
  };

  // Export to CSV
  const handleExportCSV = () => {
    setLoading('export');
    
    try {
      const headers = [
        'ID',
        'Email',
        'Phone',
        'First Name',
        'Last Name',
        'Display Name',
        'Company',
        'Status',
        'Referral Code',
        'Referral Count',
        'Login Count',
        'Favorites',
        'Hazards Reported',
        'Created At',
        'Last Login'
      ];

      const rows = selectedUsers.map(user => [
        user.id,
        user.email || '',
        user.phone || '',
        user.firstName || '',
        user.lastName || '',
        user.displayName || '',
        user.company || '',
        user.subscriptionStatus,
        user.referralCode || '',
        user.referralCount || 0,
        user.loginCount || 0,
        user.favoritesCount || 0,
        user.hazardsReported || 0,
        user.createdAt ? new Date(user.createdAt as any).toISOString() : '',
        user.lastLoginAt ? new Date(user.lastLoginAt as any).toISOString() : ''
      ]);

      const csvContent = [
        headers.join(','),
        ...rows.map(row => row.map(val => `"${String(val).replace(/"/g, '""')}"`).join(','))
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `navigatorx_users_export_${new Date().toISOString().split('T')[0]}.csv`;
      link.click();
      URL.revokeObjectURL(link.href);

      toast.success(`Exported ${selectedUsers.length} users to CSV`);
    } catch (error) {
      console.error('Export failed:', error);
      toast.error('Failed to export users');
    } finally {
      setLoading(null);
    }
  };

  // Count suspended users
  const suspendedCount = selectedUsers.filter(u => u.subscriptionStatus === 'BLOCKED').length;
  const activeCount = selectedUsers.length - suspendedCount;

  return (
    <>
      {/* Floating Action Bar */}
      <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50">
        <div className="bg-gray-900 text-white rounded-xl shadow-2xl px-4 py-3 flex items-center gap-4">
          {/* Selection Count */}
          <div className="flex items-center gap-2 pr-4 border-r border-gray-700">
            <Users className="h-5 w-5 text-amber-400" />
            <span className="font-medium">{selectedUsers.length} selected</span>
            <button 
              onClick={onClearSelection}
              className="ml-1 hover:bg-gray-700 p-1 rounded"
              title="Clear selection"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2">
            {/* Extend Trial */}
            <Button
              size="sm"
              variant="secondary"
              onClick={() => setShowConfirm('extend_trial')}
              disabled={loading !== null}
              className="bg-blue-600 hover:bg-blue-700 text-white border-0"
            >
              <Clock className="h-4 w-4 mr-1" />
              Extend Trial
            </Button>

            {/* Suspend/Unsuspend */}
            {suspendedCount < selectedUsers.length && (
              <Button
                size="sm"
                variant="secondary"
                onClick={() => setShowConfirm('suspend')}
                disabled={loading !== null}
                className="bg-orange-600 hover:bg-orange-700 text-white border-0"
              >
                <Ban className="h-4 w-4 mr-1" />
                Suspend ({activeCount})
              </Button>
            )}
            {suspendedCount > 0 && (
              <Button
                size="sm"
                variant="secondary"
                onClick={() => setShowConfirm('unsuspend')}
                disabled={loading !== null}
                className="bg-green-600 hover:bg-green-700 text-white border-0"
              >
                <CheckCircle className="h-4 w-4 mr-1" />
                Unsuspend ({suspendedCount})
              </Button>
            )}

            {/* Export */}
            <Button
              size="sm"
              variant="secondary"
              onClick={handleExportCSV}
              disabled={loading !== null}
              className="bg-gray-700 hover:bg-gray-600 text-white border-0"
            >
              {loading === 'export' ? (
                <Loader2 className="h-4 w-4 mr-1 animate-spin" />
              ) : (
                <Download className="h-4 w-4 mr-1" />
              )}
              Export CSV
            </Button>

            {/* Delete */}
            <Button
              size="sm"
              variant="secondary"
              onClick={() => setShowConfirm('delete')}
              disabled={loading !== null}
              className="bg-red-600 hover:bg-red-700 text-white border-0"
            >
              <Trash2 className="h-4 w-4 mr-1" />
              Delete
            </Button>
          </div>
        </div>
      </div>

      {/* Confirmation Dialogs */}
      {showConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">
            {/* Extend Trial Confirm */}
            {showConfirm === 'extend_trial' && (
              <>
                <div className="bg-blue-500 p-4 text-white flex items-center gap-3">
                  <Clock className="h-6 w-6" />
                  <div>
                    <h3 className="font-semibold">Extend Trial</h3>
                    <p className="text-blue-100 text-sm">{selectedUsers.length} users selected</p>
                  </div>
                </div>
                <div className="p-6">
                  <p className="text-gray-600 mb-4">
                    Extend trial period for {selectedUsers.length} selected users.
                  </p>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Days to add
                    </label>
                    <div className="flex gap-2">
                      {[7, 14, 30, 60].map(days => (
                        <button
                          key={days}
                          onClick={() => setTrialDays(days)}
                          className={`flex-1 py-2 rounded-lg border ${
                            trialDays === days 
                              ? 'bg-blue-50 border-blue-500 text-blue-700' 
                              : 'border-gray-200 hover:bg-gray-50'
                          }`}
                        >
                          {days} days
                        </button>
                      ))}
                    </div>
                  </div>
                  {loading === 'extend_trial' && (
                    <div className="mb-4">
                      <div className="flex justify-between text-sm text-gray-500 mb-1">
                        <span>Progress</span>
                        <span>{progress.current}/{progress.total}</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div 
                          className="bg-blue-500 h-2 rounded-full transition-all"
                          style={{ width: `${(progress.current / progress.total) * 100}%` }}
                        />
                      </div>
                      {progress.failed > 0 && (
                        <p className="text-red-500 text-xs mt-1">{progress.failed} failed</p>
                      )}
                    </div>
                  )}
                </div>
                <div className="border-t p-4 flex justify-end gap-3 bg-gray-50">
                  <Button variant="outline" onClick={() => setShowConfirm(null)} disabled={loading !== null}>
                    Cancel
                  </Button>
                  <Button 
                    onClick={handleBulkExtendTrial}
                    disabled={loading !== null}
                    className="bg-blue-500 hover:bg-blue-600"
                  >
                    {loading === 'extend_trial' ? (
                      <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Processing...</>
                    ) : (
                      <>Extend {selectedUsers.length} Users</>
                    )}
                  </Button>
                </div>
              </>
            )}

            {/* Suspend Confirm */}
            {showConfirm === 'suspend' && (
              <>
                <div className="bg-orange-500 p-4 text-white flex items-center gap-3">
                  <Ban className="h-6 w-6" />
                  <div>
                    <h3 className="font-semibold">Suspend Users</h3>
                    <p className="text-orange-100 text-sm">{activeCount} users will be suspended</p>
                  </div>
                </div>
                <div className="p-6">
                  <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-4">
                    <div className="flex gap-3">
                      <AlertTriangle className="h-5 w-5 text-orange-600 flex-shrink-0" />
                      <p className="text-sm text-orange-800">
                        Suspended users will lose access to the app immediately. They will not be able to log in until unsuspended.
                      </p>
                    </div>
                  </div>
                  {loading === 'suspend' && (
                    <div className="mb-4">
                      <div className="flex justify-between text-sm text-gray-500 mb-1">
                        <span>Progress</span>
                        <span>{progress.current}/{progress.total}</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div 
                          className="bg-orange-500 h-2 rounded-full transition-all"
                          style={{ width: `${(progress.current / progress.total) * 100}%` }}
                        />
                      </div>
                    </div>
                  )}
                </div>
                <div className="border-t p-4 flex justify-end gap-3 bg-gray-50">
                  <Button variant="outline" onClick={() => setShowConfirm(null)} disabled={loading !== null}>
                    Cancel
                  </Button>
                  <Button 
                    onClick={handleBulkSuspend}
                    disabled={loading !== null}
                    className="bg-orange-500 hover:bg-orange-600"
                  >
                    {loading === 'suspend' ? (
                      <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Suspending...</>
                    ) : (
                      <>Suspend {activeCount} Users</>
                    )}
                  </Button>
                </div>
              </>
            )}

            {/* Unsuspend Confirm */}
            {showConfirm === 'unsuspend' && (
              <>
                <div className="bg-green-500 p-4 text-white flex items-center gap-3">
                  <CheckCircle className="h-6 w-6" />
                  <div>
                    <h3 className="font-semibold">Unsuspend Users</h3>
                    <p className="text-green-100 text-sm">{suspendedCount} users will be unsuspended</p>
                  </div>
                </div>
                <div className="p-6">
                  <p className="text-gray-600 mb-4">
                    Unsuspended users will regain access to the app. Their subscription status will be set to expired and they will need to resubscribe.
                  </p>
                  {loading === 'unsuspend' && (
                    <div className="mb-4">
                      <div className="flex justify-between text-sm text-gray-500 mb-1">
                        <span>Progress</span>
                        <span>{progress.current}/{suspendedCount}</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div 
                          className="bg-green-500 h-2 rounded-full transition-all"
                          style={{ width: `${(progress.current / suspendedCount) * 100}%` }}
                        />
                      </div>
                    </div>
                  )}
                </div>
                <div className="border-t p-4 flex justify-end gap-3 bg-gray-50">
                  <Button variant="outline" onClick={() => setShowConfirm(null)} disabled={loading !== null}>
                    Cancel
                  </Button>
                  <Button 
                    onClick={handleBulkUnsuspend}
                    disabled={loading !== null}
                    className="bg-green-500 hover:bg-green-600"
                  >
                    {loading === 'unsuspend' ? (
                      <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Processing...</>
                    ) : (
                      <>Unsuspend {suspendedCount} Users</>
                    )}
                  </Button>
                </div>
              </>
            )}

            {/* Delete Confirm */}
            {showConfirm === 'delete' && (
              <>
                <div className="bg-red-500 p-4 text-white flex items-center gap-3">
                  <Trash2 className="h-6 w-6" />
                  <div>
                    <h3 className="font-semibold">Delete Users</h3>
                    <p className="text-red-100 text-sm">{selectedUsers.length} users will be permanently deleted</p>
                  </div>
                </div>
                <div className="p-6">
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                    <div className="flex gap-3">
                      <AlertTriangle className="h-5 w-5 text-red-600 flex-shrink-0" />
                      <div className="text-sm text-red-800">
                        <p className="font-medium">This action cannot be undone!</p>
                        <p className="mt-1">All user data including favorites, history, referrals, and notifications will be permanently deleted.</p>
                      </div>
                    </div>
                  </div>
                  <div className="max-h-40 overflow-y-auto border rounded-lg mb-4">
                    {selectedUsers.map(user => (
                      <div key={user.id} className="px-3 py-2 border-b last:border-0 flex justify-between items-center text-sm">
                        <span>{user.displayName || user.email || user.phone || user.id}</span>
                        <button
                          onClick={() => onRemoveFromSelection(user.id)}
                          className="text-gray-400 hover:text-red-500"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                  {loading === 'delete' && (
                    <div className="mb-4">
                      <div className="flex justify-between text-sm text-gray-500 mb-1">
                        <span>Deleting...</span>
                        <span>{progress.current}/{progress.total}</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div 
                          className="bg-red-500 h-2 rounded-full transition-all"
                          style={{ width: `${(progress.current / progress.total) * 100}%` }}
                        />
                      </div>
                      {progress.failed > 0 && (
                        <p className="text-red-500 text-xs mt-1">{progress.failed} failed</p>
                      )}
                    </div>
                  )}
                </div>
                <div className="border-t p-4 flex justify-end gap-3 bg-gray-50">
                  <Button variant="outline" onClick={() => setShowConfirm(null)} disabled={loading !== null}>
                    Cancel
                  </Button>
                  <Button 
                    onClick={handleBulkDelete}
                    disabled={loading !== null}
                    className="bg-red-600 hover:bg-red-700"
                  >
                    {loading === 'delete' ? (
                      <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Deleting...</>
                    ) : (
                      <>Delete {selectedUsers.length} Users Permanently</>
                    )}
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}
